/**
 * WebhookDeliveryLogPage.jsx — Per-subscription delivery log + Redrive.
 * Backend: GET /api/v1/external/webhooks/deliveries (with subscription_id param)
 *          POST /api/v1/external/webhooks/deliveries/:id/redrive
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Tag,
  Button,
  Space,
  Drawer,
  Tooltip,
  Select,
  Popconfirm,
  message as antdMessage,
} from 'antd';
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  RedoOutlined,
  EyeOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import webhookApi from '../../../../../api/webhook.api';

const STATUS_COLORS = {
  pending: 'default',
  in_progress: 'blue',
  delivered: 'green',
  failed: 'orange',
  dead: 'red',
};

const API_KEY_STORAGE = 'mos_api_key';

export default function WebhookDeliveryLogPage() {
  const { id: subscriptionId } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0 });
  const [statusFilter, setStatusFilter] = useState();
  const [drawer, setDrawer] = useState({ open: false, row: null });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {};
      if (statusFilter) filters.status = statusFilter;
      const res = await webhookApi.listDeliveries(subscriptionId, filters, pagination.page, pagination.limit);
      const body = res.data;
      // API shape: { success, data: [...], meta: { count } }
      const items = Array.isArray(body?.data)
        ? body.data
        : body?.data?.items || body?.entity?.deliveries || body?.entity?.items || [];
      const total = body?.meta?.count ?? body?.data?.total ?? body?.entity?.total ?? items.length;
      setRows(Array.isArray(items) ? items : []);
      setPagination((p) => ({ ...p, total }));
    } catch (err) {
      console.error('Failed to load deliveries', err);
      antdMessage.error('Could not load delivery log.');
    } finally {
      setLoading(false);
    }
  }, [subscriptionId, statusFilter, pagination.page, pagination.limit]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRedrive = async (row) => {
    try {
      await webhookApi.redriveDelivery(row.delivery_id || row.id);
      antdMessage.success('Delivery re-enqueued.');
      fetchData();
    } catch (err) {
      antdMessage.error(err.response?.data?.message || 'Redrive failed.');
    }
  };

  const handleRedriveAllDead = async () => {
    try {
      await webhookApi.redriveAllDead(subscriptionId);
      antdMessage.success('All dead deliveries re-enqueued.');
      fetchData();
    } catch (err) {
      antdMessage.error(err.response?.data?.message || 'Redrive failed.');
    }
  };

  const columns = [
    {
      title: 'Delivery ID',
      dataIndex: 'delivery_id',
      key: 'delivery_id',
      width: 140,
      render: (v) => <code style={{ fontSize: 12 }}>#{v}</code>,
    },
    {
      title: 'Event',
      dataIndex: 'event',
      key: 'event',
      width: 180,
      render: (e) => <Tag>{e}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (s) => <Tag color={STATUS_COLORS[s] || 'default'}>{s || 'unknown'}</Tag>,
    },
    {
      title: 'Attempts',
      dataIndex: 'attempt_count',
      key: 'attempt_count',
      width: 90,
      render: (n) => n ?? 0,
    },
    {
      title: 'HTTP',
      dataIndex: 'http_status',
      key: 'http_status',
      width: 90,
      render: (s) => s ? <Tag color={s >= 200 && s < 300 ? 'green' : 'red'}>{s}</Tag> : '—',
    },
    {
      title: 'Delivered At',
      dataIndex: 'delivered_at',
      key: 'delivered_at',
      width: 160,
      render: (v) => v ? dayjs(v).format('DD MMM HH:mm:ss') : '—',
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (v) => v ? dayjs(v).format('DD MMM HH:mm:ss') : '—',
    },
    {
      title: 'Next Retry',
      dataIndex: 'next_attempt_at',
      key: 'next_attempt_at',
      width: 160,
      render: (v) => v ? dayjs(v).format('DD MMM HH:mm:ss') : '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      fixed: 'right',
      render: (_, row) => (
        <Space size={4}>
          <Tooltip title="View payload">
            <Button size="small" icon={<EyeOutlined />} onClick={() => setDrawer({ open: true, row })} />
          </Tooltip>
          {(row.status === 'dead' || row.status === 'failed') && (
            <Tooltip title="Re-enqueue">
              <Button size="small" icon={<RedoOutlined />} onClick={() => handleRedrive(row)} />
            </Tooltip>
          )}
          {row.dead_at && row.status !== 'dead' && row.status !== 'failed' ? null : null}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/dashboard/webhooks')}>Back</Button>
      </Space>

      <Card
        title={<>Delivery Log <code style={{ fontSize: 12, marginLeft: 8 }}>{subscriptionId}</code></>}
        extra={
          <Space>
            <Select
              placeholder="Filter by status"
              allowClear
              style={{ width: 160 }}
              value={statusFilter}
              onChange={setStatusFilter}
              options={Object.keys(STATUS_COLORS).map((s) => ({ value: s, label: s }))}
            />
            <Popconfirm
              title="Re-enqueue all dead deliveries?"
              description="This will retry every delivery currently marked dead."
              onConfirm={handleRedriveAllDead}
            >
              <Button danger icon={<WarningOutlined />}>Redrive All Dead</Button>
            </Popconfirm>
            <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>Refresh</Button>
          </Space>
        }
      >
        <Table
          rowKey="delivery_id"
          columns={columns}
          dataSource={rows}
          loading={loading}
          scroll={{ x: 1100 }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            onChange: (page, limit) => setPagination((p) => ({ ...p, page, limit })),
          }}
        />
      </Card>

      <Drawer
        title={drawer.row ? `Delivery #${drawer.row.delivery_id} — ${drawer.row.event}` : 'Delivery Details'}
        open={drawer.open}
        onClose={() => setDrawer({ open: false, row: null })}
        width={640}
      >
        {drawer.row && (
          <>
            <table style={{ width: '100%', marginBottom: 16, fontSize: 13, borderCollapse: 'collapse' }}>
              {[
                ['Delivery ID', `#${drawer.row.delivery_id}`],
                ['Subscription', drawer.row.subscription_id],
                ['Event', drawer.row.event],
                ['Status', drawer.row.status],
                ['HTTP Status', drawer.row.http_status ?? '—'],
                ['Attempts', drawer.row.attempt_count ?? 0],
                ['Created At', drawer.row.created_at ? dayjs(drawer.row.created_at).format('DD MMM YYYY HH:mm:ss') : '—'],
                ['Delivered At', drawer.row.delivered_at ? dayjs(drawer.row.delivered_at).format('DD MMM YYYY HH:mm:ss') : '—'],
                ['Next Retry At', drawer.row.next_attempt_at ? dayjs(drawer.row.next_attempt_at).format('DD MMM YYYY HH:mm:ss') : '—'],
                ['Dead At', drawer.row.dead_at ? dayjs(drawer.row.dead_at).format('DD MMM YYYY HH:mm:ss') : '—'],
              ].map(([label, val]) => (
                <tr key={label}>
                  <td style={{ padding: '6px 12px 6px 0', fontWeight: 600, color: '#6b7280', width: 130, verticalAlign: 'top' }}>{label}</td>
                  <td style={{ padding: '6px 0', color: '#111827' }}>{val}</td>
                </tr>
              ))}
            </table>
            <h4 style={{ marginBottom: 8 }}>Response Snippet</h4>
            <pre style={{ background: '#f5f5f5', padding: 12, borderRadius: 6, overflow: 'auto', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {drawer.row.response_snippet || drawer.row.response_body || '—'}
            </pre>
            {drawer.row.error_message && (
              <>
                <h4 style={{ marginBottom: 8 }}>Error</h4>
                <pre style={{ background: '#fef2f2', padding: 12, borderRadius: 6, color: '#b91c1c', fontSize: 12, whiteSpace: 'pre-wrap' }}>
                  {drawer.row.error_message}
                </pre>
              </>
            )}
          </>
        )}
      </Drawer>
    </div>
  );
}
